/* eslint-disable react-hooks/exhaustive-deps */
import { FunctionComponent as F, useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from 'react-redux';
import { useAppDispatch } from "../../core/store/hooks";
import { IState } from "../../core/store/store";
import ControlledCreatableAutocomplete from "../UI/Input/ControlledCreatableAutocomplete";
import ControlledAutocomplete from "../UI/Input/ControlledAutocomplete";
import { Checkbox, FormControlLabel, FormGroup, Link, Typography } from '@mui/material';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import IconButton from "@mui/material/IconButton";
import {
  retrieveCurrenciesAsync,
  retrieveVendorsAsync } from "../../core/store/listings/listings.thunks";
import { IOption } from "@protoplan/types";
import { validatePrice, validateQuantity, validateUrl } from '../../utils/validate';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import { trimTrailing0s } from "../../utils/string";
import ClearIcon from '@mui/icons-material/Clear';
import { PRODUCTS } from "../../core/router/paths";
import { postNewListing } from "../../core/http/listing";



interface LocationState
{
  bundleProductId?: number
}


const NewListing: F = () =>
{
  const navigate = useNavigate();
  const appDispatch = useAppDispatch();
  const location = useLocation();

  const state = location.state as LocationState | undefined;
  const bundleProductId = state?.bundleProductId;

  const user = useSelector((state: IState) => state.user);
  const productId = useSelector((state: IState) => state.listings.productId);
  const vendors = useSelector((state: IState) => state.listings.vendors);
  const populatedVendorOptions = vendors.map(vendor =>
    ({ value: vendor.vendorId, label: vendor.name }));
  const currencies = useSelector((state: IState) => state.listings.currencies);
  const populatedCurrencyOptions = currencies.map(currency => ({
    value: currency.currencyId,
    label: currency.symbol ? currency.symbol + " (" + currency.code + ")" : currency.code }));
  const products = useSelector((state: IState) => state.products.products);
  const product = products?.find(p => p.productId === productId);

  const mounted = useRef<boolean>();
  useEffect(() =>
  {
    if (!mounted.current)
    {
      mounted.current = true;
      appDispatch(retrieveVendorsAsync())
        .then(() => setVendorOptions(populatedVendorOptions));
      appDispatch(retrieveCurrenciesAsync())
        .then(() => setCurrencyOptions(populatedCurrencyOptions));
    }
  });

  const [name, setName] = useState("");
  const [vendorOptions, setVendorOptions] = useState(populatedVendorOptions);
  useEffect(() => setVendorOptions(populatedVendorOptions), [vendors]);
  const [currentVendorOption, setCurrentVendorOption] = useState<IOption | null>(null);
  const [newVendor, setNewVendor] = useState<boolean>(false);
  const [currencyOptions, setCurrencyOptions] = useState(populatedCurrencyOptions);
  useEffect(() => setCurrencyOptions(populatedCurrencyOptions), [currencies]);
  const [currentCurrencyOption, setCurrentCurrencyOption] = useState<IOption | null>(null);
  const [url, setUrl] = useState("");
  const [price, setPrice] = useState("");
  const [bundle, setBundle] = useState(Boolean(bundleProductId));
  const [bundleProducts, setBundleProducts] = useState<{
    productId: number,
    productName: string,
    brandName: string,
    amount: number,
    amountUnit: string,
    quantity: string }[]>(product ? [{ ...product, quantity: "1" }] : []);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  useEffect(() =>
  {
    if(!bundleProductId || bundleProducts.find(p => p.productId === bundleProductId))
      return;

    const product = products?.find(p => p.productId === bundleProductId);
    if(!product)
      return;

    setBundleProducts([...bundleProducts.map(p => ({ ...p }))].concat({ ...product, quantity: "1" }));
  }, [bundleProductId]);

  const invalid =
    !name ||
    !currentVendorOption ||
    !(validatePrice(price) || price === "") ||
    !validateUrl(url) ||
    (newVendor && !currentCurrencyOption);

  useEffect(() =>
  {
    if(!invalid)
      setErrorMessage(undefined);
    else if(!name)
      setErrorMessage("No name entered.");
    else if(!currentVendorOption)
      setErrorMessage("No vendor chosen.");
    else if(!validateUrl(url))
      setErrorMessage("Invalid URL.");
    else if(newVendor && !currentCurrencyOption)
      setErrorMessage("Enter a currency for the new vendor.");
    else if(!validatePrice(price))
      setErrorMessage("Invalid price.");
  }, [name, currentVendorOption, price, url, newVendor, currentCurrencyOption]);

  if(!productId)
  {
    navigate(-1);
    return <></>;
  }

  const handleChangeUrl = (event: React.ChangeEvent<HTMLInputElement>) =>
  {
    setUrl(event.target.value);

    if(currentVendorOption)
      return;

    const vendorsWithUrl = vendors?.filter(v =>
      v.url &&
      event.target.value.match(new RegExp((new URL(v.url)).hostname.replace(/^www\./, ""), 'i')));

    if(vendorsWithUrl?.length != 1)
      return;

    setCurrentVendorOption(vendorOptions.find(o => o.value === vendorsWithUrl[0].vendorId) || null);
  }

  const handleChangeCurrentVendorOption = (option: IOption | null) =>
  {
    setCurrentVendorOption(option);
    (vendorOptions.filter(o => o.value === option?.value).length || !option) ?
      setNewVendor(false) :
      setNewVendor(true);
  };

  const handleSubmit = () =>
  {
    if(invalid)
      return;

    postNewListing({
      name,
      productId,
      vendorId: currentVendorOption.value,
      vendorName: currentVendorOption.label,
      currencyId: currentCurrencyOption?.value,
      price: price === "" ? undefined : parseFloat(price),
      url: url,
      bundleProducts: bundle ? bundleProducts
        .filter(p => validateQuantity(p.quantity))
        .map(p => ({ productId: p.productId, quantity: Number(p.quantity) })) : [] })
      .then(() => navigate("../", { relative: 'path', state: { retrieveListings: true } }));
  };

  return (
    <Dialog
      maxWidth='md'
      fullWidth
      PaperProps={{ sx: { p: '15px' }}}
      open={true}
      onClose={() => navigate("../../../../", { relative: 'path' })}>
      <IconButton
        disableRipple
        onClick={() => navigate("../", { relative: 'path' })}
        sx={{ display: 'inline-flex', float: 'left', width: '80px' }}>
        <ArrowBackIosNewIcon sx={{ mt: 3, ml: 1, fontSize: 65, color: 'lightGray' }}/>
      </IconButton>
      <DialogTitle sx={{ ml: 3, fontSize: '30px' }}>Add a new listing</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ width: '750px', pt: 1 }}>
          <TextField
            id='ListingName'
            label='Title*'
            variant='outlined'
            size='small'
            sx={{ width: '750px' }}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setName(event.target.value)} />
          <Stack direction='row' spacing={1}>
            <ControlledCreatableAutocomplete
              sx={{ flex: 1, flexBasis: 3 }}
              id='VendorName'
              label='Vendor*'
              width='500px'
              currentOption={currentVendorOption}
              handleChangeCurrentOption={handleChangeCurrentVendorOption}
              options={vendorOptions} />
            <FormControl>
              <InputLabel htmlFor='outlined-adornment-amount'>
                Price
              </InputLabel>
              <OutlinedInput
                size='small'
                sx={{ minWidth: 70 }}
                startAdornment={newVendor ?
                  <></> :
                  <InputAdornment position='start'>
                    {user.currencySymbol}
                  </InputAdornment>}
                id='Price'
                label='Price*'
                value={price}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setPrice(event.target.value)}
                error={!(parseFloat(price) || price === "")}/>
            </FormControl>
            {newVendor ? <ControlledAutocomplete
              label='Currency*'
              width='150px'
              currentOption={currentCurrencyOption}
              onChangeCurrentOption={setCurrentCurrencyOption}
              options={currencyOptions}/> : <></>}
          </Stack>
          <TextField
            id='Url'
            label='URL*'
            value={url}
            variant='outlined'
            size='small'
            sx={{ width: '750px' }}
            type='text'
            onChange={handleChangeUrl}
            error={!(validateUrl(url) || url === "")} />
          <FormGroup>
            <FormControlLabel control={
              <Checkbox
                checked={bundle}
                onChange={() => setBundle(!bundle)}
                inputProps={{ 'aria-label': 'controlled' }}/>} label="Bundle" />
          </FormGroup>
          {bundle ?
            <>
              {bundleProducts.map((p, i) => (
                <Stack direction='row' spacing={1} alignItems='center' key={i} sx={{ width: '750px' }}>
                  <Typography variant='body1' sx={{ flexGrow: 1 }}>
                    {p.brandName + " " + p.productName + " (" + trimTrailing0s(p.amount) + " " + p.amountUnit + ")"}
                  </Typography>
                  <TextField
                    label='Quantity'
                    value={p.quantity}
                    variant='outlined'
                    size='small'
                    sx={{ width: '100px' }}
                    error={!(validateQuantity(p.quantity) || p.quantity === "")}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    {
                      const copy = [...bundleProducts.map(p => ({ ...p }))];
                      copy[i].quantity = event.target.value;
                      setBundleProducts(copy);
                    }}/>
                  <ClearIcon
                    sx={{
                      cursor: p.productId === productId ? 'auto' : 'pointer',
                      color: p.productId === productId ? 'transparent' : 'lightGrey' }}
                    onClick={() =>
                    {
                      if(p.productId === productId)
                        return;

                      const copy = [...bundleProducts];
                      copy.splice(i, 1);
                      setBundleProducts(copy);
                    }}/>
                </Stack>
              ))}
              <Link
                variant='body2'
                underline='hover'
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(PRODUCTS, { relative: "path", state: { addNew: false } })} >
                <i>{"Add another product"}</i>
              </Link>
            </> :
            <></>}
          <Typography variant='body2' sx={{ color: 'red' }}>
            {errorMessage}
          </Typography>
          <Button
            id='Submit'
            variant='contained'
            sx={{ width: '100px' }}
            onClick={handleSubmit}
            disabled={invalid}>
            Submit
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>);
};


export default NewListing;
